import React from 'react'
import PropTypes from 'prop-types'
import ReactCrop from 'react-image-crop'
import { UI_MESSAGES } from '../AgentHeadshotUploader.constants'
import 'react-image-crop/dist/ReactCrop.css'

/**
 * CropInterface component - Displays the image cropping interface
 * @param {Object} props - Component props
 * @param {string} props.imageSrc - The image source to crop
 * @param {Object} props.crop - Current crop settings
 * @param {Function} props.onImageLoaded - Callback when image loads
 * @param {Function} props.onCropChange - Callback when crop changes
 * @param {Function} props.onCropComplete - Callback when crop is complete
 * @param {boolean} props.isProcessing - Whether image is being processed
 * @param {boolean} props.isProcessed - Whether image has been processed
 * @param {boolean} props.enableCrop - Whether cropping is enabled
 */
const CropInterface = ({
  imageSrc,
  crop,
  onImageLoaded,
  onCropChange,
  onCropComplete = () => {},
  isProcessing,
  isProcessed,
  enableCrop,
}) => {
  if (!imageSrc) {
    return null
  }

  // Don't show crop interface if crop is disabled or still processing
  if (!enableCrop || (enableCrop && !isProcessed)) {
    return null
  }

  // Determine status message
  const getStatusMessage = () => {
    if (isProcessing && enableCrop) {
      return UI_MESSAGES.PROCESSING
    }
    if (isProcessed && enableCrop) {
      return UI_MESSAGES.PROCESSED
    }
    if (enableCrop && !isProcessed) {
      return UI_MESSAGES.LOADING
    }
    return UI_MESSAGES.ORIGINAL
  }

  // Determine status color
  const getStatusColor = () => {
    if (isProcessing) return '#f39c12' // Orange
    if (isProcessed) return '#27ae60' // Green
    return '#666' // Gray
  }

  const statusMessage = getStatusMessage()
  const statusColor = getStatusColor()

  return (
    <div className='crop-interface' style={{ textAlign: 'center' }}>
      <ReactCrop
        src={imageSrc}
        crop={crop}
        ruleOfThirds
        onImageLoaded={onImageLoaded}
        onComplete={onCropComplete}
        onChange={onCropChange}
        imageStyle={{
          maxWidth: '100%',
          maxHeight: '400px',
          border: '2px solid #ddd',
          borderRadius: '8px',
        }}
      />
      <div
        className='crop-status'
        style={{
          marginTop: '10px',
          fontSize: '14px',
          color: statusColor,
          fontWeight: isProcessing || isProcessed ? 'bold' : 'normal',
          fontStyle: 'italic',
        }}
      >
        {statusMessage}
      </div>
    </div>
  )
}

CropInterface.propTypes = {
  imageSrc: PropTypes.string,
  crop: PropTypes.object.isRequired,
  onImageLoaded: PropTypes.func.isRequired,
  onCropChange: PropTypes.func.isRequired,
  onCropComplete: PropTypes.func,
  isProcessing: PropTypes.bool,
  isProcessed: PropTypes.bool,
  enableCrop: PropTypes.bool,
}

CropInterface.defaultProps = {
  onCropComplete: () => {},
  isProcessing: false,
  isProcessed: false,
  enableCrop: true,
}

export default CropInterface
